import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle, TAGLINE } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    const res = await forgotPassword(email.trim().toLowerCase());
    setLoading(false);
    if (res.ok) {
      setSent(true);
      toast.success("If that email is registered, a reset link is on its way.");
    } else {
      toast.error(res.error || "Could not process request.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col">
      <header className="w-full max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/login" className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white" data-testid="forgot-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
        <ThemeToggle />
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-10">
            <img src="/certiseal-logo.png" alt="CertiSeal" className="w-16 h-16 mx-auto mb-5 rounded-full dark:bg-white dark:p-1" />
            <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight text-slate-900 dark:text-white">Forgot password?</h1>
            <p className="mt-2 text-slate-600 dark:text-slate-400">{TAGLINE}</p>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-8">
            {sent ? (
              <div className="text-center space-y-4" data-testid="forgot-sent-panel">
                <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto">
                  <MailCheck className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h2 className="font-display font-bold text-xl text-slate-900 dark:text-white">Check your inbox</h2>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  If <span className="font-medium text-slate-900 dark:text-white">{email}</span> matches an account, we&apos;ve sent a reset link. It expires in 1 hour.
                </p>
                <Link to="/login">
                  <Button variant="outline" className="mt-2 dark:border-slate-700 dark:text-slate-100" data-testid="forgot-return-btn">
                    Return to sign in
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  Enter the admin email associated with your CertiSeal account. We&apos;ll email a secure link to choose a new password.
                </p>
                <div>
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2 block">Email</label>
                  <Input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@example.com" required
                    className="h-12 border-slate-200 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit" disabled={loading}
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 active:scale-[0.98] font-display font-semibold"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? "Sending…" : "Send reset link"}
                </Button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
